/**
 * School Mobile App Store Listing API Contract
 *
 * GET /v1/schools/:school_id/mobile-app/store-listing
 * PUT /v1/schools/:school_id/mobile-app/store-listing/:store
 *
 * A school admin reads and writes their OWN app's App Store and Google Play
 * listing copy (school app design plan U8, KTD11). The app row is resolved from
 * the school, never named by the caller, the same way the pre-login asset lane
 * resolves it.
 *
 * A SAVE REACHES NO STORE. The copy is the app's working copy; it goes to App
 * Store Connect and the Play Console when staff release the app on a line
 * (release lines U13). Until then each changed field shows as a store draft.
 *
 * THE WRITE IS PER STORE AND A STRICT ALLOWLIST. Each store accepts only the
 * school fields for that store (`SCHOOL_STORE_LISTING_FIELDS`); a field that
 * belongs to the other store is refused by name, and any other key, including the
 * fields School Journey owns (`whatsNew`, the categories, the review notes), is
 * refused as unknown. Every value is checked with the same field rules as the
 * super-admin lane (both are picks of `StoreListingRecordSchema`). The body is a partial patch: a key that is
 * present replaces the stored value, null clears it, an absent key is left alone.
 */

import { z } from 'zod'
import {
  StoreListingFieldSchema,
  StoreListingRecordSchema,
  StoreListingStoreSchema
} from '../../schemas/entities/mobile-app-store-listing'

// --- Read ---

/** Apple's school-visible fields. */
export const SchoolAppleStoreListingSchema = StoreListingRecordSchema.pick({
  title: true,
  subtitle: true,
  description: true,
  keywords: true,
  promotionalText: true,
  contactEmail: true,
  supportUrl: true,
  marketingUrl: true,
  privacyPolicyUrl: true
})
export type SchoolAppleStoreListing = z.infer<typeof SchoolAppleStoreListingSchema>

/** Google Play's school-visible fields. */
export const SchoolPlayStoreListingSchema = StoreListingRecordSchema.pick({
  title: true,
  shortDescription: true,
  description: true,
  contactEmail: true,
  supportUrl: true,
  marketingUrl: true,
  privacyPolicyUrl: true
})
export type SchoolPlayStoreListing = z.infer<typeof SchoolPlayStoreListingSchema>

/**
 * Both stores, always. A store is null when nothing has ever been saved for it
 * (or its stored row no longer parses), which is how an editor tells "never set"
 * from a listing whose fields were cleared.
 */
export const SchoolStoreListingsSchema = z.object({
  apple: SchoolAppleStoreListingSchema.nullable(),
  play: SchoolPlayStoreListingSchema.nullable()
})
export type SchoolStoreListings = z.infer<typeof SchoolStoreListingsSchema>

/** GET response. */
export const GetSchoolStoreListingsResponseSchema = SchoolStoreListingsSchema
export type GetSchoolStoreListingsResponse = z.infer<typeof GetSchoolStoreListingsResponseSchema>

// --- Write ---

const PLAY_ONLY_MESSAGE = 'is a Google Play field; it cannot be set on the App Store listing'
const APPLE_ONLY_MESSAGE = 'is an App Store field; it cannot be set on the Google Play listing'

/**
 * A key that must not be present, reported under its own name so an editor can
 * put the error on the field rather than on the form.
 */
const refusedField = (message: string) => z.never({ message }).optional()

/** A patch that names no field would save nothing and must not report success. */
const hasAtLeastOneField = (body: object) => Object.keys(body).length > 0
const EMPTY_BODY_MESSAGE = 'Send at least one field to save'

/** PUT …/store-listing/apple body. */
export const SchoolAppleStoreListingUpdateRequestSchema = SchoolAppleStoreListingSchema.partial()
  .extend({ shortDescription: refusedField(`shortDescription ${PLAY_ONLY_MESSAGE}`) })
  .strict()
  .refine(hasAtLeastOneField, { message: EMPTY_BODY_MESSAGE })
export type SchoolAppleStoreListingUpdateRequest = z.infer<typeof SchoolAppleStoreListingUpdateRequestSchema>

/** PUT …/store-listing/play body. */
export const SchoolPlayStoreListingUpdateRequestSchema = SchoolPlayStoreListingSchema.partial()
  .extend({
    subtitle: refusedField(`subtitle ${APPLE_ONLY_MESSAGE}`),
    keywords: refusedField(`keywords ${APPLE_ONLY_MESSAGE}`),
    promotionalText: refusedField(`promotionalText ${APPLE_ONLY_MESSAGE}`)
  })
  .strict()
  .refine(hasAtLeastOneField, { message: EMPTY_BODY_MESSAGE })
export type SchoolPlayStoreListingUpdateRequest = z.infer<typeof SchoolPlayStoreListingUpdateRequestSchema>

/** The body schema for a store, so a handler validates against the store in the path. */
export const SCHOOL_STORE_LISTING_UPDATE_REQUEST_SCHEMAS = {
  apple: SchoolAppleStoreListingUpdateRequestSchema,
  play: SchoolPlayStoreListingUpdateRequestSchema
} as const

export type SchoolStoreListingUpdateRequest = SchoolAppleStoreListingUpdateRequest | SchoolPlayStoreListingUpdateRequest

/**
 * PUT response: both stores as they now stand, so an editor can adopt the answer
 * as its new baseline, plus the fields this write actually changed. An empty
 * `changedFields` means the body matched what was stored and nothing was written.
 */
export const UpdateSchoolStoreListingResponseSchema = z.object({
  store: StoreListingStoreSchema,
  changedFields: z.array(StoreListingFieldSchema),
  listings: SchoolStoreListingsSchema
})
export type UpdateSchoolStoreListingResponse = z.infer<typeof UpdateSchoolStoreListingResponseSchema>

// --- Errors ---

/**
 * Failure vocabulary for both routes. `NOT_AUTHORIZED` is also what an admin of
 * another school gets, identical to an unknown school; `NO_MOBILE_APP` is a school
 * riding the shared School Journey app.
 */
export const SCHOOL_STORE_LISTING_ERROR_CODES = [
  'INVALID_PARAMS',
  'NOT_AUTHORIZED',
  'NO_MOBILE_APP',
  'INTERNAL_ERROR'
] as const
export type SchoolStoreListingErrorCode = (typeof SCHOOL_STORE_LISTING_ERROR_CODES)[number]
